import {
  addAdminFiguresCalendarDays,
  adminWallTimeToUtc,
  getAdminFiguresBusinessDayStart,
  getAdminFiguresDayStart,
  getAdminFiguresWeekStartFromMonday,
  getAdminFiguresWeekdayIndex,
  getAdminFiguresZonedParts,
  shiftAdminFiguresWeekStart,
} from "./adminFiguresTime.tsx";

export type AdminWeekRange = { start: Date; end: Date };

/** Admin: Monday at the figures reset (3:00 AM, or midnight Pacific from Oct 6 2026). */
export const getAdminStartOfWeek = (date: Date = new Date()): Date => {
  const businessDay = getAdminFiguresBusinessDayStart(date);
  const mondayIndex = getAdminFiguresWeekdayIndex(date);
  const monday = addAdminFiguresCalendarDays(businessDay, -mondayIndex);
  return getAdminFiguresWeekStartFromMonday(monday);
};

export const getAdminEndOfWeek = (date: Date = new Date()): Date => {
  const start = getAdminStartOfWeek(date);
  return new Date(shiftAdminFiguresWeekStart(start, 1).getTime() - 1);
};

export const getAdminWeekDays = (startDate: Date): Date[] => {
  const monday = getAdminFiguresDayStart(startDate);
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = addAdminFiguresCalendarDays(monday, i);
    // Noon keeps the intended weekday when shifting calendar days.
    const { year, month, day: d } = getAdminFiguresZonedParts(day);
    days.push(adminWallTimeToUtc(year, month, d, 12, 0, 0, 0));
  }
  return days;
};

export const getAdminPastWeekRanges = (maxWeeks = 28): AdminWeekRange[] => {
  const weeks: AdminWeekRange[] = [];
  const startDate = getAdminStartOfWeek(new Date());

  for (let i = 1; i < maxWeeks; i++) {
    const startOfWeek = shiftAdminFiguresWeekStart(startDate, -i);
    const endOfWeek = new Date(shiftAdminFiguresWeekStart(startOfWeek, 1).getTime() - 1);
    weeks.push({ start: startOfWeek, end: endOfWeek });
  }

  return weeks;
};

export const getAdminCurrentWeekRange = (): AdminWeekRange => ({
  start: getAdminStartOfWeek(),
  end: getAdminEndOfWeek(),
});

/** Current week runs from this Monday's figures reset through the next one. */
export const isAdminCurrentWeekRange = (start: Date): boolean => {
  const current = getAdminStartOfWeek();
  const a = getAdminFiguresZonedParts(start);
  const b = getAdminFiguresZonedParts(current);
  return a.year === b.year && a.month === b.month && a.day === b.day;
};

export const getAdminWeekRangeWeeksAgo = (weeksAgo = 0): AdminWeekRange => {
  const start = shiftAdminFiguresWeekStart(getAdminStartOfWeek(), -weeksAgo);
  return {
    start,
    end: new Date(shiftAdminFiguresWeekStart(start, 1).getTime() - 1),
  };
};
